import * as React from "react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input" // Import the base Input

// Extend Input props to explicitly include readOnly, though it should be inherited
interface EditableInputProps extends React.ComponentProps<typeof Input> {
  readOnly?: boolean;
}

const EditableInput = React.forwardRef<
  HTMLInputElement,
  EditableInputProps
>(
  ({ className, readOnly, ...props }, ref) => {
    if (readOnly) {
      const displayValue = props.value ?? props.defaultValue ?? "";
      return (
        <>
          <div
            className={cn(
              "flex w-full bg-transparent", // base layout
              "text-base pl-0 pr-3 py-2", // text and padding
              "border-0", // no border except bottom
              "border-b border-transparent",
              "whitespace-pre-wrap break-words", // wrap long text
              "cursor-pointer", // indicate clickability
              "min-h-[40px]", // maintain height behavior
              "rounded-none", // no rounding
              className
            )}
            onClick={props.onClick as unknown as React.MouseEventHandler<HTMLDivElement>}
          >
            {displayValue !== "" ? (
              displayValue
            ) : (
              <span className="text-gray-400">{props.placeholder}</span>
            )}
          </div>
          <input type="hidden" readOnly value={displayValue} />
        </>
      );
    }

    return (
      <Input
        ref={ref}
        readOnly={readOnly} // Pass readOnly prop
        className={cn(
          "flex w-full bg-transparent", // Keep bg transparent, flex, width
          "text-base pl-0 pr-3 py-2", // Explicitly set padding: NO left, YES right/vertical
          "border-0", // Explicitly remove default border
          "border-b border-transparent", // Add transparent bottom border
          // Only show focus border when not readOnly; if readOnly, override ring and borders
          !readOnly
            ? "focus-visible:border-primary"
            : "cursor-default focus:border-transparent focus-visible:border-transparent focus:ring-0 focus-visible:ring-0",
          "focus-visible:outline-none", // Remove default outline
          "disabled:cursor-not-allowed", // Base disabled style (opacity comes from Input)
          "disabled:opacity-100", // Override base opacity
          "h-auto min-h-[40px]", // Keep height behavior
          "rounded-none", // Explicitly remove rounded corners
          className // Allow overriding/adding classes
        )}
        {...props}
      />
    )
  }
);
EditableInput.displayName = "EditableInput";

export { EditableInput } 